import { useCallback, useEffect, useId, useState, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, WifiOff } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { useLanguage } from '../../i18n/language.js';
import { newIdempotencyKey } from '../../workspace/api.js';
import {
  activateMerchantRecommendation,
  fetchMerchantRecommendationReveal,
  type MerchantRecommendationRevealView,
} from '../../merchant/api.js';

const COPY = {
  en: {
    pageTitle: 'Three directions for this Production Line',
    support:
      'Shaped by the videos you liked and skipped. Choose one — you can change it before you activate.',
    line: 'Production Line',
    back: 'Production Lines',
    language: 'العربية',
    signOut: 'Sign out',
    recommended: 'Recommended',
    choose: 'Choose this direction',
    chosen: 'Direction selected',
    changed: 'Direction changed',
    hook: 'Hook',
    treatment: 'Treatment',
    message: 'Message',
    cta: 'Call to action',
    rationaleShow: 'Why this is recommended',
    limitation: 'Important limitation',
    summary: 'Direction selected',
    change: 'Change direction',
    noteLabel: 'Standing note',
    optional: 'Optional',
    noteHelp: 'Leave it empty and Mouja will handle the details.',
    notePlaceholder: '',
    activate: 'Activate Production Line',
    activating: 'Activating…',
    activated: 'Production Line activated',
    activationFailed:
      'The Production Line was not activated. Your direction and note are still here.',
    retry: 'Try again',
    preparingTitle: 'Preparing your recommendations',
    preparingBody:
      'Mouja is combining your business, competitors, purpose, and taste for this Production Line.',
    unavailableTitle: 'Recommendations are temporarily unavailable',
    unavailableBody:
      'Your Production Line is safe. Check your connection and try again.',
    offline: 'You’re offline. Your choice is kept here but cannot be activated yet.',
    skip: 'Skip to content',
  },
  ar: {
    pageTitle: 'ثلاثة اتجاهات لخط الإنتاج هذا',
    support:
      'انصاغت من المقاطع اللي عجبتك واللي تخطيتها. اختر واحد — وتقدر تغيّره قبل التفعيل.',
    line: 'خط الإنتاج',
    back: 'خطوط الإنتاج',
    language: 'English',
    signOut: 'تسجيل الخروج',
    recommended: 'المقترح الأول',
    choose: 'اختر هذا الاتجاه',
    chosen: 'انختار الاتجاه',
    changed: 'انغيّر الاتجاه',
    hook: 'الافتتاحية',
    treatment: 'المعالجة البصرية',
    message: 'الرسالة',
    cta: 'الدعوة إلى الإجراء',
    rationaleShow: 'ليش هذا الاتجاه',
    limitation: 'ملاحظة مهمة',
    summary: 'انختار الاتجاه',
    change: 'غيّر الاتجاه',
    noteLabel: 'ملاحظة مستمرة',
    optional: 'اختياري',
    noteHelp: 'خلّها فاضية وموجة تتكفّل بالتفاصيل.',
    notePlaceholder: '',
    activate: 'فعّل خط الإنتاج',
    activating: 'جاري التفعيل…',
    activated: 'انفعّل خط الإنتاج',
    activationFailed: 'خط الإنتاج ما انفعّل. اتجاهك وملاحظتك لسه محفوظين هنا.',
    retry: 'جرّب مرة ثانية',
    preparingTitle: 'نجهّز توصياتك',
    preparingBody: 'موجة تجمع معلومات نشاطك ومنافسيك وهدفك وذوقك لخط الإنتاج هذا.',
    unavailableTitle: 'التوصيات مو متاحة مؤقتًا',
    unavailableBody: 'خط الإنتاج محفوظ. تأكد من اتصالك وجرّب مرة ثانية.',
    offline: 'أنت مو متصل. اختيارك محفوظ هنا بس ما نقدر نفعّله الحين.',
    skip: 'تخطّى للمحتوى',
  },
} as const;

type Direction = MerchantRecommendationRevealView['directions'][number];

export function RecommendationRevealPage(): ReactNode {
  const { productionLineId } = useParams<{ productionLineId: string }>();
  const { language } = useLanguage();
  const copy = COPY[language];
  const headingId = useId();
  const [view, setView] = useState<MerchantRecommendationRevealView | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [activating, setActivating] = useState(false);
  const [activationFailed, setActivationFailed] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [announcement, setAnnouncement] = useState('');

  const load = useCallback(async () => {
    if (productionLineId === undefined) {
      setLoadFailed(true);
      setLoading(false);
      return;
    }
    try {
      const next = await fetchMerchantRecommendationReveal(productionLineId);
      setView(next);
      setSelected((current) => current ?? next.selectedDirectionArtifactId);
      setNote((current) =>
        current.length > 0 ? current : (next.standingInstruction ?? ''),
      );
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [productionLineId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (view?.state !== 'PREPARING') return undefined;
    const timer = window.setInterval(() => void load(), 4_000);
    return () => {
      window.clearInterval(timer);
    };
  }, [load, view?.state]);

  useEffect(() => {
    const offline = () => {
      setOnline(false);
    };
    const onlineAgain = () => {
      setOnline(true);
      void load();
    };
    window.addEventListener('offline', offline);
    window.addEventListener('online', onlineAgain);
    return () => {
      window.removeEventListener('offline', offline);
      window.removeEventListener('online', onlineAgain);
    };
  }, [load]);

  const choose = (directionId: string) => {
    const changed = selected !== null && selected !== directionId;
    setSelected(directionId);
    setActivationFailed(false);
    setAnnouncement(changed ? copy.changed : copy.chosen);
  };

  const activate = async () => {
    if (
      productionLineId === undefined ||
      view?.recommendationSetId === null ||
      view?.recommendationSetId === undefined ||
      selected === null ||
      activating ||
      !online
    ) {
      return;
    }
    setActivating(true);
    setActivationFailed(false);
    try {
      const next = await activateMerchantRecommendation(
        productionLineId,
        {
          expectedRecommendationSetId: view.recommendationSetId,
          expectedConfigurationId: view.configurationId,
          selectedDirectionArtifactId: selected,
          standingInstruction: note.trim().length === 0 ? null : note,
        },
        newIdempotencyKey(),
      );
      setView(next);
      setSelected(next.selectedDirectionArtifactId ?? selected);
      setAnnouncement(copy.activated);
    } catch {
      setActivationFailed(true);
    } finally {
      setActivating(false);
    }
  };

  const selectedDirection =
    view?.directions.find((direction) => direction.directionArtifactId === selected) ??
    null;
  const ready = view?.state === 'READY' || view?.state === 'ACTIVATED';
  const activated = view?.state === 'ACTIVATED';

  return (
    <div className="mj-reveal-page">
      <a className="mj-skip-link" href="#recommendation-main">
        {copy.skip}
      </a>

      <div id="recommendation-main" className="mj-reveal-main">
        {!online ? (
          <p className="mj-reveal-connection" role="status">
            <WifiOff aria-hidden="true" /> {copy.offline}
          </p>
        ) : null}

        {loading || view?.state === 'PREPARING' ? (
          <RevealState title={copy.preparingTitle} body={copy.preparingBody} busy />
        ) : null}
        {loadFailed || view?.state === 'TEMPORARILY_UNAVAILABLE' ? (
          <RevealState title={copy.unavailableTitle} body={copy.unavailableBody}>
            <button type="button" onClick={() => void load()}>
              {copy.retry}
            </button>
          </RevealState>
        ) : null}

        {ready ? (
          <>
            <section className="mj-reveal-intro">
              <p className="mj-reveal-eyebrow">{copy.line}</p>
              <h1 id={headingId}>{copy.pageTitle}</h1>
              <p>{copy.support}</p>
            </section>

            <div
              className="mj-reveal-directions"
              role="radiogroup"
              aria-labelledby={headingId}
            >
              {view.directions.map((direction, index) => (
                <DirectionCard
                  key={direction.directionArtifactId}
                  direction={direction}
                  recommended={index === 0}
                  selected={direction.directionArtifactId === selected}
                  disabled={activated}
                  copy={copy}
                  onChoose={() => {
                    choose(direction.directionArtifactId);
                  }}
                />
              ))}
            </div>

            {selectedDirection !== null ? (
              <section
                className="mj-reveal-activation"
                aria-labelledby="selected-heading"
              >
                <div className="mj-reveal-selected-summary">
                  <div>
                    <p id="selected-heading">{copy.summary}</p>
                    <strong>{selectedDirection.videoIdea}</strong>
                  </div>
                  {!activated ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSelected(null);
                        setAnnouncement('');
                      }}
                    >
                      {copy.change}
                    </button>
                  ) : null}
                </div>

                <label className="mj-reveal-note">
                  <span>
                    {copy.noteLabel} <small>{copy.optional}</small>
                  </span>
                  <textarea
                    dir="auto"
                    rows={3}
                    value={note}
                    readOnly={activated}
                    placeholder={copy.notePlaceholder}
                    onChange={(event) => {
                      setNote(event.target.value);
                    }}
                  />
                  <small>{activated ? '' : copy.noteHelp}</small>
                </label>

                {activated ? (
                  <p className="mj-reveal-success" role="status">
                    <CheckCircle2 aria-hidden="true" /> {copy.activated}
                  </p>
                ) : (
                  <button
                    type="button"
                    className="mj-reveal-activate"
                    disabled={activating || !online}
                    onClick={() => void activate()}
                  >
                    {activating ? copy.activating : copy.activate}
                  </button>
                )}
                {activationFailed ? (
                  <p className="mj-reveal-error" role="alert">
                    {copy.activationFailed}
                  </p>
                ) : null}
              </section>
            ) : null}
          </>
        ) : null}
      </div>
      <p className="mj-visually-hidden" aria-live="polite" aria-atomic="true">
        {announcement}
      </p>
    </div>
  );
}

function DirectionCard({
  direction,
  recommended,
  selected,
  disabled,
  copy,
  onChoose,
}: {
  readonly direction: Direction;
  readonly recommended: boolean;
  readonly selected: boolean;
  readonly disabled: boolean;
  readonly copy: (typeof COPY)[keyof typeof COPY];
  readonly onChoose: () => void;
}): ReactNode {
  return (
    <article
      className={`mj-reveal-card${recommended ? ' is-dominant' : ''}${selected ? ' is-selected' : ''}`}
      onClick={(event) => {
        const target = event.target;
        if (
          !disabled &&
          target instanceof Element &&
          target.closest('button,input,summary,details,textarea,a,label') === null
        ) {
          onChoose();
        }
      }}
    >
      <div className="mj-reveal-card-kicker">
        <p className="mj-reveal-rank">
          {copy.line} {String(direction.rank)}
        </p>
        {recommended ? (
          <p className="mj-reveal-recommended">
            <span aria-hidden="true">
              <i />
              <i />
              <i />
            </span>{' '}
            {copy.recommended}
          </p>
        ) : null}
      </div>
      <h2>{direction.videoIdea}</h2>
      <dl>
        <DirectionRow label={copy.hook} value={direction.hook} />
        <DirectionRow label={copy.treatment} value={direction.visualTreatment} />
        <DirectionRow label={copy.message} value={direction.coreMessage} />
        <DirectionRow label={copy.cta} value={direction.callToAction} />
      </dl>
      {direction.limitations[0] !== undefined ? (
        <p className="mj-reveal-limitation">
          <AlertTriangle aria-hidden="true" />
          <span>
            <strong>{copy.limitation}</strong> {direction.limitations[0]}
          </span>
        </p>
      ) : null}
      <details>
        <summary>
          {copy.rationaleShow} <ChevronDown aria-hidden="true" />
        </summary>
        <p>{direction.rankingRationale}</p>
      </details>
      <label className="mj-reveal-choice">
        <input
          type="radio"
          name="recommendation-direction"
          value={direction.directionArtifactId}
          checked={selected}
          disabled={disabled}
          onChange={onChoose}
        />
        <span>{selected ? copy.chosen : copy.choose}</span>
      </label>
    </article>
  );
}

function DirectionRow({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function RevealState({
  title,
  body,
  busy = false,
  children,
}: {
  readonly title: string;
  readonly body: string;
  readonly busy?: boolean;
  readonly children?: ReactNode;
}) {
  return (
    <section className="mj-reveal-state" role={busy ? 'status' : undefined}>
      {busy ? <span className="mj-reveal-state-pulse" aria-hidden="true" /> : null}
      <h1>{title}</h1>
      <p>{body}</p>
      {children}
    </section>
  );
}
